'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

async function fetchFavorites(address: string): Promise<string[]> {
  const res = await fetch(`/api/favorites/${address}`);
  if (!res.ok) throw new Error('Failed to fetch favorites');
  const data = await res.json();
  return data.favorites ?? [];
}

export function useFavorites(address: string | null | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ['favorites', address?.toLowerCase()];

  const { data: favoritesList = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchFavorites(address!),
    enabled: !!address,
    staleTime: 30_000,
  });

  const favorites = new Set(favoritesList);

  const toggleFavorite = useCallback(async (poolId: string) => {
    if (!address) return;

    const current: string[] = queryClient.getQueryData(queryKey) ?? [];
    const isFav = current.includes(poolId);

    // Optimistic update
    queryClient.setQueryData(queryKey, isFav
      ? current.filter(id => id !== poolId)
      : [poolId, ...current],
    );

    try {
      await fetch(`/api/favorites/${address}`, {
        method: isFav ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId }),
      });
    } catch {
      // Revert on failure
      queryClient.setQueryData(queryKey, current);
    }
  }, [address, queryClient, queryKey]);

  return { favorites, toggleFavorite, isLoading };
}
